import { ChangeDetectionStrategy, Component, effect, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MaskitoDirective } from '@maskito/angular';
import { type MaskitoOptions } from '@maskito/core';
import { TuiInput, TuiTextfield } from '@taiga-ui/core';
import { TuiInputNumber } from '@taiga-ui/kit';
import { formatPace, parsePace } from '@shared/charts/chart-axis-units';

export type PerformanceLimitsFieldKey =
  | 'male_upper'
  | 'male_lower'
  | 'male_stdDev'
  | 'female_upper'
  | 'female_lower'
  | 'female_stdDev';

export const PERFORMANCE_LIMITS_FIELD_KEYS: readonly PerformanceLimitsFieldKey[] = [
  'male_upper',
  'male_lower',
  'male_stdDev',
  'female_upper',
  'female_lower',
  'female_stdDev',
];

export interface INumberPerformanceLimitsValue
  extends Record<PerformanceLimitsFieldKey, number | null> {
  kind: 'number';
}

export interface IPacePerformanceLimitsValue extends Record<PerformanceLimitsFieldKey, string> {
  kind: 'pace';
}

/** Either every field is a plain number, or every field is a masked "m:ss.s" pace string. */
export type IPerformanceLimitsValue = INumberPerformanceLimitsValue | IPacePerformanceLimitsValue;

const MALE_FIELDS = PERFORMANCE_LIMITS_FIELD_KEYS.slice(0, 3);
const FEMALE_FIELDS = PERFORMANCE_LIMITS_FIELD_KEYS.slice(3, 6);

const PACE_MASK: MaskitoOptions = {
  mask: ({ value }) => {
    const digitsBeforeColon = (value.split(':')[0] ?? '').replace(/\D/g, '').length || 1;
    const minuteDigits = Math.min(Math.max(digitsBeforeColon, 1), 3);
    return [...Array(minuteDigits).fill(/\d/), ':', /[0-5]/, /\d/, '.', /\d/];
  },
};

function parseValue(value: IPerformanceLimitsValue, field: PerformanceLimitsFieldKey): number | null {
  return value.kind === 'pace' ? parsePace(value[field]) : value[field];
}

function isStdDevField(field: PerformanceLimitsFieldKey): boolean {
  return field === 'male_stdDev' || field === 'female_stdDev';
}

/** Std-dev fields are additionally invalid when they resolve to 0 or a non-finite number. */
function isFieldInvalid(value: IPerformanceLimitsValue, field: PerformanceLimitsFieldKey): boolean {
  const parsed = parseValue(value, field);
  if (parsed === null) return true;
  return isStdDevField(field) ? parsed === 0 || !Number.isFinite(parsed) : false;
}

/** abs(upper - lower) / 6 — `null` when either input isn't parseable yet. */
function computeStdDev(upper: number | null, lower: number | null): number | null {
  return upper === null || lower === null ? null : Math.abs(upper - lower) / 6;
}

/**
 * Shared 4-row/3-col Male/Female × Upper/Lower/Std-dev editor for one datapoint's performance
 * limits. Fills its container. Values are plain numbers or "m:ss.s" pace strings depending on
 * `value().kind` — the caller (which knows the sport's reference system) decides which; this
 * component has no unit awareness of its own.
 *
 * Std dev auto-recomputes from that gender's upper/lower whenever either changes, but the user
 * can still type over it directly — editing std dev itself never triggers a recompute.
 */
@Component({
  selector: 'app-performance-limits-table',
  templateUrl: 'performance-limits-table.html',
  styleUrl: 'performance-limits-table.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, MaskitoDirective, TuiInput, TuiInputNumber, TuiTextfield],
})
export class PerformanceLimitsTableComponent {
  readonly value = input.required<IPerformanceLimitsValue>();
  readonly editable = input(true);

  readonly valueChange = output<IPerformanceLimitsValue>();
  readonly invalidChange = output<boolean>();

  protected readonly maleFields = MALE_FIELDS;
  protected readonly femaleFields = FEMALE_FIELDS;
  protected readonly mask = PACE_MASK;

  constructor() {
    // Reports validity for the current value on every change, including the initial bind —
    // a plain computed() can't be read by the parent without a viewChild per row, so this is
    // surfaced as an output instead.
    effect(() => {
      const current = this.value();
      this.invalidChange.emit(PERFORMANCE_LIMITS_FIELD_KEYS.some((field) => isFieldInvalid(current, field)));
    });
  }

  protected fieldInvalid(field: PerformanceLimitsFieldKey): boolean {
    return isFieldInvalid(this.value(), field);
  }

  protected updateNumberField(field: PerformanceLimitsFieldKey, raw: number | null): void {
    const current = this.value();
    if (current.kind !== 'number') return;
    this.emitChange(this.recomputeStdDev({ ...current, [field]: raw }, field));
  }

  protected updatePaceField(field: PerformanceLimitsFieldKey, raw: string): void {
    const current = this.value();
    if (current.kind !== 'pace') return;
    this.emitChange(this.recomputeStdDev({ ...current, [field]: raw }, field));
  }

  private emitChange(next: IPerformanceLimitsValue): void {
    this.valueChange.emit(next);
  }

  /** Auto-fills std dev from upper/lower for whichever gender changed; leaves it alone otherwise. */
  private recomputeStdDev(
    value: IPerformanceLimitsValue,
    changedField: PerformanceLimitsFieldKey,
  ): IPerformanceLimitsValue {
    if (isStdDevField(changedField)) return value;

    const isMale = changedField.startsWith('male');
    const upperField: PerformanceLimitsFieldKey = isMale ? 'male_upper' : 'female_upper';
    const lowerField: PerformanceLimitsFieldKey = isMale ? 'male_lower' : 'female_lower';
    const stdDevField: PerformanceLimitsFieldKey = isMale ? 'male_stdDev' : 'female_stdDev';

    const stdDev = computeStdDev(parseValue(value, upperField), parseValue(value, lowerField));
    if (stdDev === null) return value;

    if (value.kind === 'pace') {
      return { ...value, [stdDevField]: formatPace(stdDev) };
    }
    return { ...value, [stdDevField]: stdDev };
  }
}
